import { z } from 'zod';
import { CHART_COLOR_TOKENS, CHART_NEUTRAL_TOKEN } from '../analytics/chart-colors';
import { USAGE_VIEW_NAME } from '../constants';
import { countWords } from '../count-words';
// Analytics dashboards ("views") and their charts. A custom chart is SQL over the calls and chats tables, run on the
// org's analytics lake for the open dashboard's date range and filters; the chart config says how to draw the result.
const MAX_ROWS_SHOWN = 20;
const MAX_CELL_CHARS = 200;
const dataKeySchema = z.string().describe('A column the SQL returns, spelled exactly like its alias');
// `catch` drops any other color rather than failing, so charts saved before still parse for the chart check.
const fillSchema = z
    .enum([...CHART_COLOR_TOKENS, CHART_NEUTRAL_TOKEN])
    .optional()
    .catch(undefined)
    .describe('A palette color by its --chart-N: 1 green, 2 blue, 3 orange, 4 violet, 5 olive, 6 cyan, 7 amber, 8 rose; --chart-neutral is grey. Left out, series take the palette in order');
const seriesSchema = z.object({
    dataKey: dataKeySchema,
    fill: fillSchema,
    yAxisId: z.literal('right').optional().describe('Put this series on a second axis on the right')
});
const fillMissingDatesSchema = z
    .boolean()
    .optional()
    .describe('For a date x-axis: show days with no rows as zero');
export const viaChartConfigSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('value'),
        value: dataKeySchema.describe('The one column shown as a big number')
    }),
    z.object({
        type: z.literal('stat'),
        value: dataKeySchema.describe('The headline column, read from the last row'),
        unit: z.string().optional().describe('A short unit after the headline, such as "%" or "calls"'),
        icon: z
            .enum([
            'activity',
            'phone',
            'check',
            'clock',
            'dollar',
            'star',
            'message',
            'users',
            'trending-up',
            'shield',
            'calendar',
            'mail',
            'zap',
            'target',
            'file',
            'alert'
        ])
            .optional()
            .describe('phone for calls, check for completed or resolved, star for ratings such as CSAT, clock for durations, dollar for money, calendar for bookings, alert for errors; activity when nothing fits'),
        trend: z
            .object({ chart: z.enum(['bar', 'line']), xAxis: dataKeySchema, yAxis: dataKeySchema })
            .optional()
            .describe('A small chart under the headline, drawn from every row, such as the value per day'),
        showChange: z.boolean().optional().describe("Show the change between the trend's last two points"),
        target: z.number().optional().describe('A goal, drawn as a dashed line across the trend')
    }),
    z.object({
        type: z.literal('bar'),
        xAxis: dataKeySchema,
        yAxis: z.object({ dataKey: dataKeySchema, fill: fillSchema }),
        fillMissingDates: fillMissingDatesSchema
    }),
    z.object({
        type: z.literal('stacked-bar'),
        xAxis: dataKeySchema,
        bars: z.array(z.object({ dataKey: dataKeySchema, fill: fillSchema })).min(1),
        fillMissingDates: fillMissingDatesSchema
    }),
    z.object({
        type: z.literal('line'),
        xAxis: dataKeySchema,
        lines: z.array(seriesSchema).min(1),
        fillMissingDates: fillMissingDatesSchema
    }),
    z.object({
        type: z.literal('combo'),
        xAxis: dataKeySchema,
        bars: z.array(seriesSchema).min(1),
        lines: z.array(seriesSchema).min(1),
        fillMissingDates: fillMissingDatesSchema
    }),
    z.object({
        type: z.literal('table'),
        columns: z
            .array(z.object({
            dataKey: dataKeySchema,
            miniBar: z.boolean().optional().describe('Draw a small bar behind a numeric column'),
            exclude: z.boolean().optional().describe('Hide the column but keep it for drilldown')
        }))
            .min(1)
    }),
    z.object({
        type: z.literal('sankey'),
        sourceKey: dataKeySchema,
        targetKey: dataKeySchema,
        valueKey: dataKeySchema
    }),
    z.object({
        type: z.literal('markdown'),
        content: z.string().describe('Text for the dashboard, such as a heading or a note; takes no SQL')
    })
]);
const builtInChartSchema = z.enum([
    'total-calls',
    'connection-rate',
    'conversion-rate',
    'average-duration',
    'call-volume'
]);
const filterSchema = z.object({
    mode: z.enum(['include', 'exclude']),
    values: z.array(z.string()).min(1)
});
const viewFiltersSchema = z.object({
    agent: filterSchema.optional().describe('Agent ids from list_agents'),
    campaign: filterSchema.optional().describe('Campaign names')
});
const chartViewSchema = z.object({
    chartId: z.number(),
    title: z.string(),
    description: z.string().optional(),
    builtIn: z.string().optional().describe('Which built-in chart this is; built-ins have no SQL'),
    sql: z.string().optional(),
    config: z.record(z.string(), z.unknown()),
    position: z.object({ x: z.number(), y: z.number(), width: z.number(), height: z.number() })
});
export const listAnalyticsViewsToolSchema = {
    name: 'list_analytics_views',
    description: "List the organization's analytics dashboards (views).",
    inputSchema: z.object({}),
    outputSchema: z.object({ views: z.array(z.object({ viewId: z.string(), name: z.string() })) })
};
export const getAnalyticsViewToolSchema = {
    name: 'get_analytics_view',
    description: 'Read a dashboard: the agent and campaign filters it opens with, and every chart with its SQL, configuration and place on the grid (8 columns wide).',
    inputSchema: z.object({ viewId: z.string() }),
    outputSchema: z.object({
        viewId: z.string(),
        name: z.string(),
        filters: viewFiltersSchema,
        connectedRateName: z.string(),
        conversionRateName: z.string(),
        charts: z.array(chartViewSchema)
    })
};
export const createAnalyticsViewToolSchema = {
    name: 'create_analytics_view',
    description: 'Create a dashboard. It starts with the five built-in charts (Calls Made, Connected Rate, Conversion Rate, Avg Call Duration, Call Volume); remove any the user does not want.',
    inputSchema: z.object({ name: z.string().min(1).max(100) }),
    outputSchema: z.object({ viewId: z.string(), name: z.string() })
};
export const updateAnalyticsViewToolSchema = {
    name: 'update_analytics_view',
    description: "Rename a dashboard, or set the agent and campaign filters it opens with for everyone. Leave out what shouldn't change.",
    inputSchema: z.object({
        viewId: z.string(),
        name: z.string().min(1).max(100).optional(),
        filters: viewFiltersSchema.optional().describe('Replaces its saved filters; {} clears them')
    }),
    outputSchema: z.object({ viewId: z.string() })
};
export const deleteAnalyticsViewToolSchema = {
    name: 'delete_analytics_view',
    description: 'Delete a dashboard and all of its charts. This cannot be undone.',
    inputSchema: z.object({ viewId: z.string() }),
    outputSchema: z.object({ viewId: z.string(), name: z.string() })
};
export const runAnalyticsQueryToolSchema = {
    name: 'run_analytics_query',
    description: "Run a SELECT against the calls and chats loaded on the dashboard the user has open, for its date range and filters, and see the columns and first rows. Use it to explore the data and to check a chart's SQL before saving it. At most 200 rows come back. The database is Postgres, so write Postgres and keep queries light: one running past 15 seconds is stopped. Only works while a dashboard is open.",
    inputSchema: z.object({ sql: z.string() }),
    outputSchema: z.object({
        columns: z.array(z.string()),
        rows: z.array(z.record(z.string(), z.unknown())).describe('The first 20 rows, long values shortened'),
        rowCount: z.number(),
        moreRows: z.boolean().describe('More rows than rowCount exist; only the first 200 were fetched'),
        ms: z.number().describe('How long the query took'),
        textNumberColumns: z
            .array(z.string())
            .describe('Columns whose numbers come back as text; cast them with ::int or ::float8 before charting them'),
        loaded: z
            .object({ calls: z.number(), chats: z.number() })
            .describe('Conversations the dashboard loaded; zero means its date range and filters hold none'),
        dateRange: z.object({ from: z.string(), to: z.string() }),
        transcriptsLoaded: z
            .boolean()
            .describe('Transcripts load after the rest; queries over them are incomplete until this is true')
    })
};
const DASHBOARD_COLUMNS = 8;
/** Grid cells a chart takes on the dashboard, which is 8 columns wide. */
export const CHART_SIZES = {
    small: { width: 2, height: 1 },
    square: { width: 2, height: 2 },
    medium: { width: 4, height: 2 },
    wide: { width: 8, height: 2 },
    tall: { width: 4, height: 3 }
};
/**
 * Where a new chart goes: the first spot, reading left to right and top to bottom, that fits it beside the
 * dashboard's charts. Small charts then sit side by side rather than stacking down the left edge.
 */
export const firstOpenSpot = (charts, { width, height }) => {
    const isOpen = (x, y) => charts.every((chart) => x >= chart.gridX + chart.gridWidth ||
        chart.gridX >= x + width ||
        y >= chart.gridY + chart.gridHeight ||
        chart.gridY >= y + height);
    for (let cell = 0;; cell++) {
        const x = cell % DASHBOARD_COLUMNS;
        const y = Math.floor(cell / DASHBOARD_COLUMNS);
        if (x + width <= DASHBOARD_COLUMNS && isOpen(x, y))
            return { gridX: x, gridY: y };
    }
};
export const saveChartToolSchema = {
    name: 'save_chart',
    description: 'Add a chart to a dashboard, or replace one by its chartId. The dashboard must be open: the SQL runs there first, and nothing is saved if it fails, returns none of the columns the config draws, or returns numbers as text. The SQL is a single SELECT over strada_calls_history and strada_chats_history. Name the chart in 1–3 words and describe it in 5–10 words. After saving, the chart is pointed out to the user.',
    inputSchema: z.object({
        viewId: z.string(),
        chartId: z.number().int().optional().describe('The chart to replace; leave out to add one'),
        title: z
            .string()
            .min(1)
            .max(60)
            .describe('1–3 words besides joining words like "by", such as "Booking Rate" or "Transfers by Day". Leave the agent or campaign out; the description says which calls it counts.'),
        description: z
            .string()
            .max(120)
            .optional()
            .describe('5–10 words saying what it counts, such as "Calls where book_meeting returned confirmed"'),
        sql: z.string().describe('Empty for a markdown chart'),
        config: viaChartConfigSchema,
        size: z
            .enum(['small', 'square', 'medium', 'wide', 'tall'])
            .optional()
            .describe('small (2×1) for one number, square (2×2) for a stat with a trend, medium (4×2), wide (8×2, the full width) for trends over days, tall (4×3) for long tables. New charts are sized for their type when left out; a replaced chart keeps its size.')
    }),
    outputSchema: z.object({ viewId: z.string(), chartId: z.number() })
};
export const removeChartToolSchema = {
    name: 'remove_chart',
    description: 'Remove a chart from a dashboard.',
    inputSchema: z.object({ viewId: z.string(), chartId: z.number().int() }),
    outputSchema: z.object({ viewId: z.string(), chartId: z.number() })
};
export const addBuiltInChartToolSchema = {
    name: 'add_built_in_chart',
    description: 'Add a built-in chart to a dashboard: total-calls (Calls Made), connection-rate, conversion-rate, average-duration or call-volume. Each can appear once per dashboard.',
    inputSchema: z.object({ viewId: z.string(), chart: builtInChartSchema }),
    outputSchema: z.object({ viewId: z.string(), chartId: z.number() })
};
/** The Via anchor on a dashboard chart, so Via can point at a chart it added. */
export const analyticsChartAnchorId = (chartId) => `analytics-chart-${chartId}`;
/** Dashboards users see; the Usage view is Strada's own. */
export const isCustomerView = ({ viewName }) => viewName !== USAGE_VIEW_NAME;
export const describeAnalyticsView = (view) => ({
    viewId: view.viewId,
    name: view.viewName,
    filters: view.filters ?? {},
    connectedRateName: view.connectedDisplayName,
    conversionRateName: view.convertedDisplayName,
    charts: view.charts.map((chart) => ({
        chartId: chart.id,
        title: chart.chartTitle,
        ...(chart.chartDescription && { description: chart.chartDescription }),
        ...(chart.builtInKey && { builtIn: chart.builtInKey }),
        ...(chart.chartSql && { sql: chart.chartSql }),
        config: chart.chartConfig,
        position: { x: chart.gridX, y: chart.gridY, width: chart.gridWidth, height: chart.gridHeight }
    }))
});
const withoutComments = (sql) => sql.replace(/--[^\n]*/g, '').replace(/\/\*[\s\S]*?\*\//g, '');
/**
 * Charts may only read. A saved chart's SQL also runs in the email report worker, where a write would change the data
 * later charts in the same report read.
 */
export const readOnlyQueryProblem = (sql) => {
    const statement = withoutComments(sql).trim().replace(/;\s*$/, '');
    if (!/^(select|with)\b/i.test(statement))
        return 'Charts can only run a SELECT (or WITH ... SELECT).';
    if (statement.includes(';'))
        return 'Run one statement at a time.';
    return undefined;
};
/** The columns a chart config draws from. */
export const chartDataKeys = (config) => {
    switch (config.type) {
        case 'value':
            return [config.value];
        case 'stat':
            return config.trend ? [config.value, config.trend.xAxis, config.trend.yAxis] : [config.value];
        case 'bar':
            return [config.xAxis, config.yAxis.dataKey];
        case 'stacked-bar':
            return [config.xAxis, ...config.bars.map(({ dataKey }) => dataKey)];
        case 'line':
            return [config.xAxis, ...config.lines.map(({ dataKey }) => dataKey)];
        case 'combo':
            return [config.xAxis, ...[...config.bars, ...config.lines].map(({ dataKey }) => dataKey)];
        case 'table':
            return config.columns.map(({ dataKey }) => dataKey);
        case 'sankey':
            return [config.sourceKey, config.targetKey, config.valueKey];
        case 'markdown':
            return [];
    }
};
/**
 * The columns a chart scales along an axis, which must come back as numbers: Recharts compares text as text, so "9"
 * would sit above "10". Value and stat headlines show as written.
 */
export const chartMeasureKeys = (config) => {
    switch (config.type) {
        case 'bar':
            return [config.yAxis.dataKey];
        case 'stacked-bar':
            return config.bars.map(({ dataKey }) => dataKey);
        case 'line':
            return config.lines.map(({ dataKey }) => dataKey);
        case 'combo':
            return [...config.bars, ...config.lines].map(({ dataKey }) => dataKey);
        case 'sankey':
            return [config.valueKey];
        case 'stat':
            return config.trend ? [config.trend.yAxis] : [];
        case 'value':
        case 'table':
        case 'markdown':
            return [];
    }
};
// Joining words keep a short name readable, as in "Transfers by Day", so they don't count toward its length.
const JOINING_WORDS = new Set([
    'a',
    'an',
    'the',
    'by',
    'per',
    'of',
    'and',
    '&',
    'in',
    'on',
    'for',
    'to',
    'vs',
    'vs.'
]);
const nameWordCount = (name) => name
    .trim()
    .split(/\s+/)
    .filter((word) => word && !JOINING_WORDS.has(word.toLowerCase())).length;
/** Why a chart's name or description isn't the short, plain kind a person would write, if it isn't. */
export const chartNamingProblem = ({ title, description, config }) => {
    const titleWords = nameWordCount(title);
    if (titleWords > 3) {
        return `"${title}" has ${titleWords} words besides joining words like "by". Name the chart in 1–3 words, as a professional would, such as "Booking Rate" or "Transfers by Day"; say how it's counted in the description.`;
    }
    if (config.type === 'markdown')
        return undefined;
    if (!description?.trim()) {
        return 'Describe the chart in 5–10 words saying what it counts, such as "Calls where book_meeting returned confirmed".';
    }
    const descriptionWords = countWords(description);
    if (descriptionWords < 5 || descriptionWords > 10) {
        return `The description is ${descriptionWords} words. Say what the chart counts in 5–10 words, without repeating the title or restating units such as "as a percentage".`;
    }
    return undefined;
};
/** The size a new chart gets when Via doesn't choose one. */
export const defaultChartSize = (config) => {
    if (config.type === 'value')
        return 'small';
    if (config.type === 'stat')
        return config.trend ? 'square' : 'small';
    if (['line', 'combo', 'stacked-bar', 'markdown'].includes(config.type))
        return 'wide';
    return 'medium';
};
/** Why a chart can't be saved as written, given the columns its SQL returned. */
export const chartProblem = ({ sql, config }, columns) => {
    if (config.type === 'markdown')
        return sql.trim() ? 'A markdown chart takes no SQL.' : undefined;
    const queryProblem = readOnlyQueryProblem(sql);
    if (queryProblem)
        return queryProblem;
    if (!columns)
        return undefined;
    const missing = chartDataKeys(config).filter((key) => !columns.includes(key));
    return missing.length > 0
        ? `The config uses ${missing.map((key) => `"${key}"`).join(', ')}, but the SQL returns ${columns.map((column) => `"${column}"`).join(', ')}.`
        : undefined;
};
const shortenCell = (value) => {
    if (value === null || typeof value === 'number' || typeof value === 'boolean')
        return value;
    const text = typeof value === 'string' ? value : JSON.stringify(value);
    return text.length > MAX_CELL_CHARS ? `${text.slice(0, MAX_CELL_CHARS)}…` : text;
};
/** A query result trimmed for Via: every column, and the first rows with long cells shortened. */
export const describeQueryRows = (columns, rows) => ({
    columns,
    rows: rows
        .slice(0, MAX_ROWS_SHOWN)
        .map((row) => Object.fromEntries(columns.map((column) => [column, shortenCell(row[column])]))),
    rowCount: rows.length
});
/** A chart as the app's chart endpoints take it. */
export const toChartBody = ({ title, description, sql, config }) => ({
    chartSql: config.type === 'markdown' ? '' : sql,
    chartTitle: title,
    ...(description && { chartDescription: description }),
    chartConfig: config
});
/** The filters to save for an update_analytics_view call, in the app's shape. */
export const toViewFilters = (filters) => ({
    ...(filters.agent && { agent: filters.agent }),
    ...(filters.campaign && { campaign: filters.campaign })
});
//# sourceMappingURL=analytics.js.map